/**
 * Public country pick-rate doc + in-memory singleton subscription for the client.
 *
 * Path: `competitions/{tid}/stats/countryPickRates` (public read; CF-written).
 * Module-level cache so multiple hooks share one `onSnapshot`.
 */
import { doc, type Firestore, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { ACTIVE_TOURNAMENT } from '../constants';
import type { Country } from './country';

export const COUNTRY_PICK_RATES_DOC_ID = 'countryPickRates';

export type CountryPickRatesDoc = {
  teamsSelectedCount: number;
  pickRates: Partial<Record<Country, number>>;
  updatedAt: Timestamp | null;
};

type PickRates = Partial<Record<Country, number>> | undefined;
type StoreListener = () => void;

let cachedPickRates: PickRates;
let cachedTeamsSelectedCount: number | undefined;
let activeDb: Firestore | null = null;
let unsubscribe: Unsubscribe | null = null;
const listeners = new Set<StoreListener>();

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function setCachedCountryPickRates(doc: CountryPickRatesDoc | null) {
  cachedPickRates = doc?.pickRates;
  cachedTeamsSelectedCount = doc?.teamsSelectedCount;
  notifyListeners();
}

export function subscribeCountryPickRatesStore(listener: StoreListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCountryPickRatesSnapshot(): PickRates {
  return cachedPickRates;
}

export function getTeamsSelectedCountSnapshot(): number | undefined {
  return cachedTeamsSelectedCount;
}

export function countryPickRatesDocRef(db: Firestore) {
  return doc(db, 'competitions', ACTIVE_TOURNAMENT, 'stats', COUNTRY_PICK_RATES_DOC_ID);
}

export function subscribeCountryPickRates(
  db: Firestore,
  onData: (doc: CountryPickRatesDoc | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    countryPickRatesDocRef(db),
    (snap) => {
      if (!snap.exists()) {
        onData(null);
        return;
      }
      const data = snap.data() as CountryPickRatesDoc;
      onData({
        teamsSelectedCount: data.teamsSelectedCount ?? 0,
        pickRates: data.pickRates ?? {},
        updatedAt: data.updatedAt ?? null,
      });
    },
    (error) => {
      console.error('[countryPickRates]', error);
      onError?.(error);
    },
  );
}

/** Start (or keep) a single shared Firestore listener for public pick rates. */
export function ensureCountryPickRatesSubscription(db: Firestore) {
  if (unsubscribe && activeDb === db) return;

  unsubscribe?.();
  unsubscribe = null;
  activeDb = db;

  unsubscribe = subscribeCountryPickRates(db, (doc) => {
    setCachedCountryPickRates(doc);
  });
}
